import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Save,
  Plus,
  Trash2,
  Sparkles,
  BookOpen,
  Settings,
  AlertTriangle,
  ArrowLeft,
  Loader2,
  Info,
  Wrench,
  ShieldAlert,
  Clock,
  Gauge,
  X, // X is already imported, good.
} from "lucide-react";

import { sopsAPI, machinesAPI } from "../api";
import toast from "react-hot-toast";
import clsx from "clsx"; // Import clsx for conditional classes

const SOPForm = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const aiDraft = location.state?.draft;
  const sourceLogId = aiDraft?.sourceLogId || null;

  const [formData, setFormData] = useState({
    title: aiDraft?.title || "",
    description: aiDraft?.description || "",
    machine_id: aiDraft?.machineId || "",
    category: aiDraft?.category || "Operation",
    estimatedDuration: aiDraft?.estimatedDuration || 30,
    skillLevel: aiDraft?.skillLevel || "Intermediate",
    safetyWarnings:
      aiDraft?.safetyWarnings && aiDraft?.safetyWarnings.length > 0
        ? aiDraft.safetyWarnings
        : [""], // Ensure at least one empty string
    requiredTools:
      aiDraft?.requiredTools && aiDraft?.requiredTools.length > 0
        ? aiDraft.requiredTools
        : [""], // Ensure at least one empty string
    steps:
      aiDraft?.steps && aiDraft?.steps.length > 0
        ? aiDraft.steps
        : [
            {
              instruction: "",
              warning: "",
              requiresPhoto: false,
              isCheckpoint: false,
            },
          ],
  });

  // Fetch machines for the dropdown
  const { data: machines } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () => machinesAPI.getAll().then((res) => res.data.data),
  });

  const createSopMutation = useMutation({
    mutationFn: (data) =>
      sopsAPI.create({
        ...data,
        sourceMaintenanceLogId: sourceLogId,
      }),
    onSuccess: () => {
      toast.success("Standard Operating Procedure established!");
      navigate(sourceLogId ? `/maintenance/${sourceLogId}` : "/sops");
    },
    onError: (err) => toast.error("Failed to save SOP: " + err.message),
  });

  // Dynamic List Handlers (Safety & Tools)
  const handleAddListItem = (field) => {
    setFormData((prev) => ({ ...prev, [field]: [...prev[field], ""] }));
  };

  const handleUpdateListItem = (field, index, value) => {
    const newList = [...formData[field]];
    newList[index] = value;
    setFormData((prev) => ({ ...prev, [field]: newList }));
  };

  const handleRemoveListItem = (field, index) => {
    const newList = formData[field].filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      [field]: newList.length > 0 ? newList : [""],
    })); // Ensure at least one input always exists
  };

  // Step Handlers
  const handleAddStep = () => {
    setFormData((prev) => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          instruction: "",
          warning: "",
          requiresPhoto: false,
          isCheckpoint: false,
        },
      ],
    }));
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...formData.steps];
    newSteps[index][field] = value;
    setFormData((prev) => ({ ...prev, steps: newSteps }));
  };

  const toggleMarker = (index, field) => {
    const newSteps = [...formData.steps];
    newSteps[index][field] = !newSteps[index][field];
    setFormData((prev) => ({ ...prev, steps: newSteps }));
  };

  const handleRemoveStep = (index) => {
    const newSteps = formData.steps.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      steps:
        newSteps.length > 0
          ? newSteps
          : [
              {
                instruction: "",
                warning: "",
                requiresPhoto: false,
                isCheckpoint: false,
              },
            ],
    })); // Ensure at least one step always exists
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
      {/* Header Area */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            {aiDraft ? "Review AI-Synthesized SOP" : "Create New SOP"}
          </h1>
          <p className="text-slate-500 mt-1">
            Define detailed steps and safety protocols for standard operations.
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          createSopMutation.mutate(formData);
        }}
        className="space-y-6"
      >
        {" "}
        {/* Wrap content in form and connect to submit */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Section: Metadata */}
          <div className="lg:col-span-12 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
            <h3 className="font-bold text-lg text-slate-800 border-b pb-3 mb-4 border-slate-100">
              SOP Metadata
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block mb-1 text-sm font-medium text-slate-700">
                  SOP Title*
                </label>
                <input
                  className="input"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="e.g. Daily Spindle Warmup"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="block mb-1 text-sm font-medium text-slate-700">
                  Target Machine*
                </label>
                <div className="relative">
                  <select
                    className={clsx(
                      "input appearance-none pr-10",
                      aiDraft && "opacity-70 cursor-not-allowed bg-slate-100",
                    )}
                    value={formData.machine_id}
                    disabled={!!aiDraft}
                    onChange={(e) =>
                      setFormData({ ...formData, machine_id: e.target.value })
                    }
                    required
                  >
                    <option value="">Select a machine...</option>
                    {machines?.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                  <Settings className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div className="space-y-2">
                <label className="block mb-1 text-sm font-medium text-slate-700">
                  Category
                </label>
                <select
                  className="input appearance-none pr-10"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                >
                  <option value="Operation">Operation</option>
                  <option value="Maintenance">Maintenance</option>
                  <option value="Safety">Safety</option>
                  <option value="Quality">Quality</option>
                  <option value="Troubleshooting">Troubleshooting</option>
                  <option value="Calibration">Calibration</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="block mb-1 text-sm font-medium text-slate-700 flex items-center gap-1">
                  <Clock className="w-3 h-3 text-slate-400" /> Est. Duration
                  (min)
                </label>
                <input
                  type="number"
                  className="input"
                  value={formData.estimatedDuration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      estimatedDuration: e.target.value,
                    })
                  }
                  min="0"
                />
              </div>
              <div className="space-y-2">
                <label className="block mb-1 text-sm font-medium text-slate-700 flex items-center gap-1">
                  <Gauge className="w-3 h-3 text-slate-400" /> Skill Level
                </label>
                <select
                  className="input appearance-none pr-10"
                  value={formData.skillLevel}
                  onChange={(e) =>
                    setFormData({ ...formData, skillLevel: e.target.value })
                  }
                >
                  <option value="Beginner">Beginner</option>
                  <option value="Intermediate">Intermediate</option>
                  <option value="Advanced">Advanced</option>
                </select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block mb-1 text-sm font-medium text-slate-700">
                Description
              </label>
              <textarea
                className="input min-h-[80px]"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Provide a brief overview of what this SOP covers."
              />
            </div>
          </div>

          {/* Middle Section: Safety & Tools */}
          <div className="lg:col-span-6 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-rose-500" /> Safety
                Warnings
              </h3>
              <button
                onClick={() => handleAddListItem("safetyWarnings")}
                className="btn btn-secondary btn-sm"
                type="button"
              >
                <Plus className="w-4 h-4" /> Add Warning
              </button>
            </div>
            <div className="space-y-3">
              {formData.safetyWarnings.map((warning, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    className="input flex-1"
                    value={warning}
                    onChange={(e) =>
                      handleUpdateListItem(
                        "safetyWarnings",
                        idx,
                        e.target.value,
                      )
                    }
                    placeholder="e.g., Wear safety glasses"
                  />
                  <button
                    onClick={() => handleRemoveListItem("safetyWarnings", idx)}
                    className="p-2 text-slate-500 hover:text-red-500 hover:bg-slate-200 rounded-full transition-colors"
                    type="button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-blue-500" /> Required Tools
              </h3>
              <button
                onClick={() => handleAddListItem("requiredTools")}
                className="btn btn-secondary btn-sm"
                type="button"
              >
                <Plus className="w-4 h-4" /> Add Tool
              </button>
            </div>
            <div className="space-y-3">
              {formData.requiredTools.map((tool, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    className="input flex-1"
                    value={tool}
                    onChange={(e) =>
                      handleUpdateListItem("requiredTools", idx, e.target.value)
                    }
                    placeholder="e.g., Torque wrench"
                  />
                  <button
                    onClick={() => handleRemoveListItem("requiredTools", idx)}
                    className="p-2 text-slate-500 hover:text-red-500 hover:bg-slate-200 rounded-full transition-colors"
                    type="button"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom Section: Procedure Steps */}
          <div className="lg:col-span-12 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Info className="w-5 h-5 text-slate-500" />
                Procedure Steps
              </h3>
              <button
                onClick={handleAddStep}
                className="btn btn-secondary btn-sm gap-2 mt-4 sm:mt-0"
                type="button"
              >
                <Plus className="w-4 h-4" /> Add New Step
              </button>
            </div>
            <div className="space-y-6">
              {formData.steps.map((step, idx) => (
                <div
                  key={idx}
                  className="relative p-6 bg-slate-50 border border-slate-200 rounded-2xl space-y-5"
                >
                  <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-[10px] font-black uppercase flex items-center gap-1">
                      Step {idx + 1}
                    </span>
                    <button
                      onClick={() => handleRemoveStep(idx)}
                      className="p-2 text-slate-500 hover:text-red-500 hover:bg-slate-200 rounded-full transition-colors"
                      type="button"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  <textarea
                    className="input min-h-[120px]"
                    placeholder="Enter detailed instruction for this step..."
                    value={step.instruction}
                    onChange={(e) =>
                      updateStep(idx, "instruction", e.target.value)
                    }
                    rows="4"
                    required
                  />
                  <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between pt-3 border-t border-slate-100">
                    <input
                      className="input w-full md:flex-1"
                      placeholder="Step-specific warning (optional)..."
                      value={step.warning}
                      onChange={(e) =>
                        updateStep(idx, "warning", e.target.value)
                      }
                    />
                    <div className="flex gap-6 items-center flex-wrap mt-3 md:mt-0">
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          checked={step.isCheckpoint}
                          onChange={() => toggleMarker(idx, "isCheckpoint")}
                        />
                        <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600">
                          Checkpoint
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                          checked={step.requiresPhoto}
                          onChange={() => toggleMarker(idx, "requiresPhoto")}
                        />
                        <span className="text-sm font-medium text-slate-700 group-hover:text-blue-600">
                          Requires Photo
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Action Buttons (Moved to bottom, mirroring DocumentUpload) */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200 mt-8">
          {" "}
          {/* Added margin top */}
          <button
            type="button"
            onClick={() => navigate("/sops")}
            className="btn btn-ghost"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              createSopMutation.isPending ||
              !formData.title ||
              !formData.machine_id
            }
            className="btn btn-primary"
          >
            {createSopMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save SOP
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SOPForm;
